import type { LocationStatus } from "../types";
import { defaultFetchHeaders, fetchJson } from "../../utils";
import type { BillingStatus, SubscriptionSummary } from "../../user/subscription";
import type { DirectDepositPlan } from "../../user/subscription";
import type { CommissionSettings, MachineThresholdScope } from "../types";
import { SUPERUSER_WRITE_HEADER, SUPERUSER_REASON_HEADER, SUPERUSER_HEADER_ENABLED } from "../state/superuserContext";

export type AdministrationSubscriptionState = "dormant" | "trial" | "active" | "grace" | "past_due" | "canceling" | "canceled" | "exempt";
export type AdministrationOrganizationSort = "priority" | "title_asc" | "title_desc" | "created_desc" | "created_asc" | "expected_charge_asc" | "expected_charge_desc";
export type AdministrationOrganizationMetric = "new" | "subscription_revenue" | "expected_charge" | "attention";
export type AdministrationOrganizationActivityType = "sale" | "task" | "telemetry" | "login";
export type AdministrationExecutionRolloutSetting = "execution_sessions" | "location_ingestion" | "live_web" | "background_location" | "history";
export type AdministrationExecutionRollout = Pick<AdministrationDashboard["recentOrganizations"][number], "executionSessionsEnabled" | "executionLocationIngestionEnabled" | "executionLiveWebEnabled" | "executionBackgroundLocationEnabled" | "executionHistoryEnabled">;
export type AdministrationOrganizationQuery = {
  q: string;
  states: AdministrationSubscriptionState[];
  sort: AdministrationOrganizationSort;
  metric: AdministrationOrganizationMetric | "";
  limit: number;
  offset: number;
};

export type AdministrationDashboard = {
  generatedAt: string;
  range: { preset: "this_month"; start: string; end: string; previousStart: string; previousEnd: string; label: string };
  organizations: { total: number; createdInRange: number; previousCreatedInRange: number };
  users: { organizationUsers: number };
  subscriptions: Record<"dormant" | "trial" | "active" | "grace" | "pastDue" | "canceling" | "canceled" | "exempt", number>;
  revenue: { currentRangeCents: number; previousRangeCents: number; successfulChargeCount: number; failedChargeCount: number };
  attention: Array<{ organizationId: string; organizationTitle: string; ownerEmail: string; reason: "trial_ending" | "grace" | "past_due" | "failed_charge"; relevantAt: string | null }>;
  recentOrganizations: Array<{
    id: string; title: string; logoUrl: string | null; ownerEmail: string; createdAt: string;
    subscriptionState: AdministrationSubscriptionState; userCount: number;
    activeMachineCount: number; machineCount: number;
    activeLocationCount: number; locationCount: number;
    pendingOrganizationInviteCount: number; pendingContactInviteCount: number;
    grossSalesCents: number; previousGrossSalesCents: number;
    expectedNextChargeCents: number; nextBillingAt: string | null;
    lastActivityAt: string | null; lastActivityType: AdministrationOrganizationActivityType | null;
    lastActivityActor: string | null;
    attentionReasons: string[];
    executionSessionsEnabled: boolean;
    executionLocationIngestionEnabled: boolean;
    executionLiveWebEnabled: boolean;
    executionBackgroundLocationEnabled: boolean;
    executionHistoryEnabled: boolean;
    metricMatches?: { new: boolean; subscriptionRevenue: boolean; liveSales: boolean; expectedCharge: boolean; attention: boolean } | null;
  }>;
};

export type AdministrationOrganizationPage = {
  generatedAt: string;
  range: AdministrationDashboard["range"];
  items: AdministrationDashboard["recentOrganizations"];
  total: number;
  organizationTotal: number;
  statusTotals: Array<{ state: AdministrationSubscriptionState; count: number }>;
  summary: Record<"new" | "subscriptionRevenue" | "liveSales" | "expectedCharges" | "attention", {
    amountCents: number | null;
    count: number;
    previousAmountCents: number | null;
    previousCount: number | null;
  }>;
  limit: number;
  offset: number;
  sort: AdministrationOrganizationSort;
};

export type AdministrationOrganizationDetail = {
  generatedAt: string;
  range: AdministrationDashboard["range"];
  organization: AdministrationDashboard["recentOrganizations"][number];
};

export type AdministrationOrganizationPeople = {
  generatedAt: string;
  members: Array<{ id: string; firstName: string; lastName: string; email: string; phone: string | null; role: "owner" | "admin" | "operator"; active: boolean; lastLoginAt: string | null }>;
  memberTotal: number;
  memberLimit: number;
  memberOffset: number;
  invitations: Array<{ id: string; email: string; role: string; target: "organization" | "location"; locationId: string | null; locationName: string | null; invitedAt: string; expiresAt: string }>;
  invitationTotal: number;
  invitationLimit: number;
  invitationOffset: number;
};

export type AdministrationPeopleMutation =
  | { type: "change-role"; userId: string; role: "admin" | "operator" }
  | { type: "remove-member"; userId: string }
  | { type: "invite"; email: string; role: "admin" | "operator" }
  | { type: "resend-invitation"; invitationId: string }
  | { type: "revoke-invitation"; invitationId: string };

export type AdministrationNetworkLocation = {
  id: string;
  name: string;
  status: LocationStatus;
  street: string;
  city: string;
  state: string;
  zip?: string;
  country?: string;
  notes?: string;
  defaultAssignee?: AdministrationNetworkPerson | null;
  contacts?: AdministrationNetworkPerson[];
  serviceCadence?: {
    frequency?: number | string | null;
    days?: string[];
    machineThresholdScope?: MachineThresholdScope;
    revenueThreshold?: number | null;
    cashThreshold?: number | null;
    cardThreshold?: number | null;
    inventoryTrigger?: number | null;
  } | null;
  commission?: CommissionSettings | null;
  machineCount: number;
  activeMachineCount: number;
};

export type AdministrationNetworkPerson = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
};

export type AdministrationNetworkMachine = {
  id: string;
  name: string;
  type: string;
  makeModel: string | null;
  active: boolean;
  assigned: boolean;
  locationId: string | null;
  locationName: string | null;
  card?: boolean;
  cash?: boolean;
  coin?: boolean;
  cardReaderDeviceId?: string | null;
  cardReaderMachineId?: string | null;
  cardReaderManufacturer?: string | null;
  cardTransactionSurchargeCents?: number | null;
  notes?: string;
  commission?: CommissionSettings | null;
};

export type AdministrationOrganizationNetwork = {
  deviceTotal?: number;
  generatedAt: string;
  locations: AdministrationNetworkLocation[];
  locationTotal: number;
  locationLimit: number;
  locationOffset: number;
  machines: AdministrationNetworkMachine[];
  machineTotal: number;
  machineLimit: number;
  machineOffset: number;
};

export type AdministrationOrganizationBilling = {
  trialOffer?: { name: string; durationValue: number; durationUnit: "days" | "weeks" | "months" } | null;
  directDepositPlan?: DirectDepositPlan;
  generatedAt: string;
  status: BillingStatus;
  billingProfile: SubscriptionSummary["billingProfile"];
  charges: SubscriptionSummary["charges"];
  total: number;
  paidCount: number;
  paidTotalCents: number;
  limit: number;
  offset: number;
  actions: AdministrationBillingActions;
};

export type AdministrationBillingActionState = {
  visible: boolean;
  enabled: boolean;
  reason: string | null;
};

export type AdministrationBillingActions = {
  resumeSubscription: AdministrationBillingActionState;
  cancelImmediately: AdministrationBillingActionState;
  retryFailedCharges: AdministrationBillingActionState;
  managePaymentMethod: AdministrationBillingActionState;
  markExempt: AdministrationBillingActionState;
  editExemption: AdministrationBillingActionState;
  removeExemption: AdministrationBillingActionState;
};

export type AdministrationBillingMutation =
  | "resume"
  | "cancel-immediately"
  | "retry-failed-charges";

export type AdministrationBillingExemptionRequest = {
  scope?: "subscription" | "direct_deposit";
  enabled: boolean;
  reason: string;
  expiresAt: string | null;
};

export type AdministrationBillingExemptionPreview = {
  current: { enabled: boolean; reason: string | null; expiresAt: string | null };
  next: { enabled: boolean; reason: string | null; expiresAt: string | null };
  resultingSubscriptionState: AdministrationSubscriptionState;
  outcome: string;
};

export type AdministrationOrganizationChargeDetail = {
  updatedAt: string;
  waive: { visible: boolean; enabled: boolean; reason: string | null };
  generatedAt: string;
  charge: SubscriptionSummary["charges"][number];
  relatedCharges: SubscriptionSummary["charges"];
};

export type AdministrationOrganizationDeletionPreview = {
  organization: { id: string; title: string; ownerId: string; ownerEmail: string };
  counts: Record<string, number>;
  blockers: string[];
  members: Array<{
    id: string; email: string; name: string; role: "owner" | "admin" | "operator";
    deleteEligible: boolean; selectedByDefault: boolean; retainedReason: string | null;
    otherOrganizations: Array<{ id: string; title: string }>;
  }>;
  confirmationToken: string;
  expiresInSeconds: number;
};

const backend = process.env.REACT_APP_BACKEND_URL || "";

export async function fetchAdministrationDashboard(range = "this_month"): Promise<AdministrationDashboard> {
  return fetchJson(
    `${backend}/administration/dashboard?range=${encodeURIComponent(range)}`,
    { method: "GET", credentials: "include", cache: "no-store", headers: await defaultFetchHeaders() },
    "Failed to load administration dashboard",
  );
}

export async function fetchAdministrationOrganizations(
  { q = "", states = [], sort = "priority", metric = "", limit = 25, offset = 0 }: Partial<AdministrationOrganizationQuery> = {},
  signal?: AbortSignal,
): Promise<AdministrationOrganizationPage> {
  const params = new URLSearchParams({ range: "this_month", sort, limit: String(limit), offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  if (states.length) params.set("states", states.join(","));
  if (metric) params.set("metric", metric);
  return fetchJson(
    `${backend}/administration/organizations?${params}`,
    { method: "GET", credentials: "include", headers: await defaultFetchHeaders(), signal },
    "Failed to load organizations",
  );
}

export async function fetchAdministrationOrganization(id: string): Promise<AdministrationOrganizationDetail> {
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(id)}?range=this_month`,
    { method: "GET", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to load organization",
  );
}

export async function mutateAdministrationExecutionSessions(
  id: string,
  enabled: boolean,
): Promise<void> {
  await fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(id)}/execution-sessions`,
    { method: "POST", credentials: "include", headers: await defaultFetchHeaders(), body: JSON.stringify({ enabled }) },
    `Failed to ${enabled ? "enable" : "disable"} execution sessions`,
  );
}

export async function mutateAdministrationExecutionRollout(
  id: string,
  setting: AdministrationExecutionRolloutSetting,
  enabled: boolean,
): Promise<void> {
  await fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(id)}/execution-rollout`,
    { method: "POST", credentials: "include", headers: await defaultFetchHeaders(), body: JSON.stringify({ setting, enabled }) },
    `Failed to ${enabled ? "enable" : "disable"} execution rollout setting`,
  );
}

export async function updateAdministrationExecutionRollout(
  id: string,
  rollout: AdministrationExecutionRollout,
): Promise<void> {
  await fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(id)}/execution-rollout`,
    { method: "PUT", credentials: "include", headers: await defaultFetchHeaders(), body: JSON.stringify(rollout) },
    "Failed to save mobile execution settings",
  );
}

export async function fetchAdministrationOrganizationPeople(
  id: string,
  { memberLimit = 25, memberOffset = 0, invitationLimit = 25, invitationOffset = 0 } = {},
): Promise<AdministrationOrganizationPeople> {
  const params = new URLSearchParams({
    member_limit: String(memberLimit),
    member_offset: String(memberOffset),
    invitation_limit: String(invitationLimit),
    invitation_offset: String(invitationOffset),
  });
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(id)}/people?${params}`,
    { method: "GET", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to load organization people",
  );
}

export async function mutateAdministrationOrganizationPeople(
  organizationId: string,
  mutation: AdministrationPeopleMutation,
): Promise<void> {
  const base = `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/people`;
  const path = mutation.type === "change-role"
    ? `/members/${encodeURIComponent(mutation.userId)}/role`
    : mutation.type === "remove-member"
      ? `/members/${encodeURIComponent(mutation.userId)}/remove`
      : mutation.type === "invite"
        ? "/invitations"
        : `/invitations/${encodeURIComponent(mutation.invitationId)}/${mutation.type === "resend-invitation" ? "resend" : "revoke"}`;
  const body = mutation.type === "change-role"
    ? { role: mutation.role }
    : mutation.type === "invite"
      ? { email: mutation.email, role: mutation.role }
      : undefined;
  await fetchJson(
    `${base}${path}`,
    { method: "POST", credentials: "include", headers: await defaultFetchHeaders(), ...(body ? { body: JSON.stringify(body) } : {}) },
    "Failed to update organization people",
  );
}

export async function fetchAdministrationOrganizationNetwork(
  id: string,
  { locationStatus = "all", machineStatus = "all", locationLimit = 25, locationOffset = 0, machineLimit = 25, machineOffset = 0 } = {},
): Promise<AdministrationOrganizationNetwork> {
  const params = new URLSearchParams({
    location_status: locationStatus,
    machine_status: machineStatus,
    location_limit: String(locationLimit),
    location_offset: String(locationOffset),
    machine_limit: String(machineLimit),
    machine_offset: String(machineOffset),
  });
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(id)}/network?${params}`,
    { method: "GET", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to load organization network",
  );
}

export async function fetchAdministrationOrganizationNetworkDirectory(
  organizationId: string,
): Promise<{ locations: AdministrationNetworkLocation[]; machines: AdministrationNetworkMachine[]; deviceTotal?: number }> {
  const pageSize = 100;
  const firstPage = await fetchAdministrationOrganizationNetwork(organizationId, {
    locationLimit: pageSize,
    machineLimit: pageSize,
  });
  const pageCount = Math.max(
    Math.ceil(firstPage.locationTotal / pageSize),
    Math.ceil(firstPage.machineTotal / pageSize),
  );
  const offsets = Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) => (index + 1) * pageSize);
  const pages = [firstPage];
  const concurrency = 4;
  for (let index = 0; index < offsets.length; index += concurrency) {
    pages.push(...await Promise.all(offsets.slice(index, index + concurrency).map((offset) =>
      fetchAdministrationOrganizationNetwork(organizationId, {
        locationLimit: pageSize,
        locationOffset: offset,
        machineLimit: pageSize,
        machineOffset: offset,
      })
    )));
  }
  return {
    deviceTotal: firstPage.deviceTotal ?? 0,
    locations: Array.from(new Map(pages.flatMap((page) => page.locations).map((location) => [location.id, location])).values()).slice(0, firstPage.locationTotal),
    machines: Array.from(new Map(pages.flatMap((page) => page.machines).map((machine) => [machine.id, machine])).values()).slice(0, firstPage.machineTotal),
  };
}

export async function fetchAdministrationOrganizationBilling(
  id: string,
  { limit = 25, offset = 0 }: { limit?: number; offset?: number } = {},
): Promise<AdministrationOrganizationBilling> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(id)}/billing?${params}`,
    { method: "GET", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to load organization billing",
  );
}

export async function fetchAdministrationOrganizationCharge(
  organizationId: string,
  chargeId: string,
): Promise<AdministrationOrganizationChargeDetail> {
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/billing/charges/${encodeURIComponent(chargeId)}`,
    { method: "GET", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to load organization charge",
  );
}

export type AdministrationChargeWaiverRequest = { reason: string; expectedUpdatedAt: string };

export async function waiveAdministrationOrganizationCharge(
  organizationId: string, chargeId: string, request: AdministrationChargeWaiverRequest,
): Promise<void> {
  const headers = new Headers(await defaultFetchHeaders());
  headers.set(SUPERUSER_WRITE_HEADER, SUPERUSER_HEADER_ENABLED);
  headers.set(SUPERUSER_REASON_HEADER, request.reason.trim());
  await fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/billing/charges/${encodeURIComponent(chargeId)}/waive`,
    { method: "POST", credentials: "include", headers, body: JSON.stringify(request) },
    "Failed to waive charge",
  );
}

export async function mutateAdministrationOrganizationBilling(
  organizationId: string,
  action: AdministrationBillingMutation,
): Promise<void> {
  await fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/billing/${action}`,
    { method: "POST", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to update organization billing",
  );
}

async function postBillingExemption(
  organizationId: string,
  endpoint: "preview" | "update",
  request: AdministrationBillingExemptionRequest,
): Promise<AdministrationBillingExemptionPreview> {
  const suffix = endpoint === "preview" ? "/preview" : "";
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/billing/exemption${suffix}`,
    {
      method: "POST",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      body: JSON.stringify(request),
    },
    endpoint === "preview" ? "Failed to preview billing exemption" : "Failed to update billing exemption",
  );
}

export const previewAdministrationBillingExemption = (
  organizationId: string,
  request: AdministrationBillingExemptionRequest,
) => postBillingExemption(organizationId, "preview", request);

export const mutateAdministrationBillingExemption = (
  organizationId: string,
  request: AdministrationBillingExemptionRequest,
) => postBillingExemption(organizationId, "update", request);

export async function updateAdministrationDirectDepositRate(
  organizationId: string, request: { monthlyFeeCents: number; reason: string }, signal?: AbortSignal,
): Promise<DirectDepositPlan> {
  return fetchJson(`${backend}/administration/organizations/${encodeURIComponent(organizationId)}/direct-deposit-rate`, {
    method: "POST", credentials: "include", headers: await defaultFetchHeaders(), body: JSON.stringify(request), signal,
  }, "Unable to update direct-deposit pricing");
}

export async function previewAdministrationOrganizationDeletion(
  organizationId: string,
): Promise<AdministrationOrganizationDeletionPreview> {
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/deletion/preview`,
    { method: "POST", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to preview organization deletion",
  );
}

export async function deleteAdministrationOrganization(
  organizationId: string,
  request: { confirmationToken: string; confirmedTitle: string; deleteUserIds: string[]; reason: string },
): Promise<{ organizationId: string; deletedUsers: Array<{ id: string; email: string }> }> {
  const reason = request.reason.trim();
  if (!reason) throw new Error("A reason is required to delete an organization.");
  // The final confirmation authorizes this administration action only; it does
  // not enable writes in the separate organization-support context.
  const headers = new Headers(await defaultFetchHeaders());
  headers.set(SUPERUSER_WRITE_HEADER, SUPERUSER_HEADER_ENABLED);
  headers.set(SUPERUSER_REASON_HEADER, reason);
  return fetchJson(
    `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/deletion`,
    { method: "POST", credentials: "include", headers, body: JSON.stringify({ ...request, reason }) },
    "Failed to delete organization",
  );
}
